import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Switch } from '../../components/ui/switch';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Mail, 
  Shield, 
  Check, 
  X, 
  Eye, 
  EyeOff,
  RefreshCw,
  Save,
  TestTube,
  AlertTriangle,
  Info,
  Wrench,
  ExternalLink,
  CheckCircle,
  XCircle,
  Clock,
  ShoppingCart
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ==================== Feature Flags Panel (v10.7.1) ====================
function FeatureFlagsPanel() {
  const [flags, setFlags] = useState({ soft_skills_enabled: false, hybrid_enabled: false, xr_enabled: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('token');
    Promise.all([
      fetch(`${API_URL}/api/scenarios/types`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
      fetch(`${API_URL}/api/xr/feature-flag`, { headers: { Authorization: `Bearer ${token}` } }).then((r) => r.json()),
    ])
      .then(([types, xr]) => setFlags({
        ...(types.feature_flags || { soft_skills_enabled: false, hybrid_enabled: false }),
        xr_enabled: !!xr.xr_enabled_global,
      }))
      .catch(() => toast.error('No se pudo cargar el estado de las feature flags'))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (key, value) => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      if (key === 'xr_enabled') {
        const res = await fetch(`${API_URL}/api/xr/feature-flag`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ xr_enabled: value }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Error');
        setFlags((prev) => ({ ...prev, xr_enabled: value }));
      } else {
        const res = await fetch(`${API_URL}/api/scenarios/feature-flags`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ [key]: value }),
        });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Error');
        const updated = await res.json();
        setFlags((prev) => ({ ...prev, ...updated }));
      }
      toast.success('Feature flag actualizada');
    } catch (e) {
      toast.error(e.message || 'No se pudo actualizar la flag');
    } finally {
      setSaving(false);
    }
  };

  const items = [
    {
      key: 'soft_skills_enabled',
      title: 'Escenarios de Habilidades Blandas',
      description: 'Permite crear escenarios centrados en comunicación, empatía y competencias no técnicas. Requiere el motor Soft Skills (Fase 2).',
      color: 'purple',
    },
    {
      key: 'hybrid_enabled',
      title: 'Escenarios Híbridos',
      description: 'Combina la simulación clínica con habilidades blandas en un mismo escenario (deterioro clínico + comunicación con familiares, etc.).',
      color: 'amber',
    },
    {
      key: 'xr_enabled',
      title: 'SimuClinic XR (Realidad Aumentada/Mixta)',
      description: 'Habilita el módulo XR: dispositivos móviles/tablet/gafas AR pueden vincularse a sesiones y enviar interacciones que disparan el motor fisiológico. Escenarios sin config XR no se ven afectados.',
      color: 'cyan',
    },
  ];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <span>🚀</span> Feature Flags
        </CardTitle>
        <CardDescription>
          Activa funcionalidades avanzadas para toda la plataforma. Solo super_admin.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="text-slate-500 text-sm py-4">Cargando…</div>
        ) : (
          items.map((f) => (
            <div
              key={f.key}
              className="flex items-start justify-between gap-4 p-4 border rounded-lg bg-slate-50"
              data-testid={`flag-row-${f.key}`}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-slate-900">{f.title}</h4>
                  <Badge className={flags[f.key] ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-600'}>
                    {flags[f.key] ? 'Activo' : 'Inactivo'}
                  </Badge>
                </div>
                <p className="text-xs text-slate-600 mt-1">{f.description}</p>
              </div>
              <Switch
                checked={!!flags[f.key]}
                onCheckedChange={(v) => toggle(f.key, v)}
                disabled={saving}
                data-testid={`toggle-${f.key}`}
              />
            </div>
          ))
        )}
        <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5">
          <Info size={12} className="mt-0.5 shrink-0" />
          Al desactivar una flag no se eliminan los escenarios existentes de ese tipo — solo se impide crear nuevos.
        </p>
      </CardContent>
    </Card>
  );
}

export default function SettingsPage() {
  const { user, isSuperAdmin } = useAuth();
  const token = localStorage.getItem('token');
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  
  // Payment test modal
  const [showPaymentTest, setShowPaymentTest] = useState(false);
  const [paymentTestStatus, setPaymentTestStatus] = useState('idle'); // idle, creating, pending, success, failed
  const [paymentTestData, setPaymentTestData] = useState(null);
  
  // Transbank config
  const [transbankConfig, setTransbankConfig] = useState({
    environment: 'integration',
    commerce_code: '',
    api_key: '',
    is_production: false
  });
  const [showTransbankKey, setShowTransbankKey] = useState(false);
  
  // Email config
  const [emailConfig, setEmailConfig] = useState({
    smtp_host: 'smtp.gmail.com',
    smtp_port: 587,
    smtp_user: 'notificaciones@simuclinic.cl',
    smtp_password: '',
    smtp_from_name: 'SimuClinic',
    smtp_configured: false
  });
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  
  // UF config
  const [ufConfig, setUfConfig] = useState({
    valor_actual: 0,
    fecha_valor_actual: null,
    fuente_actual: 'manual',
    auto_sync_enabled: true,
    dias_alerta_desactualizacion: 2
  });
  
  useEffect(() => {
    if (isSuperAdmin) {
      fetchConfigurations();
    }
  }, [isSuperAdmin]);
  
  const fetchConfigurations = async () => {
    try {
      // Fetch Transbank config
      const tbRes = await fetch(`${API_URL}/api/transbank/config`);
      if (tbRes.ok) {
        const tbData = await tbRes.json();
        setTransbankConfig(prev => ({ ...prev, ...tbData }));
      }
      
      // Fetch Email config
      const emailRes = await fetch(`${API_URL}/api/email-notifications/config`);
      if (emailRes.ok) {
        const emailData = await emailRes.json();
        setEmailConfig(prev => ({ 
          ...prev, 
          smtp_host: emailData.smtp_host ?? prev.smtp_host,
          smtp_port: emailData.smtp_port ?? prev.smtp_port,
          smtp_user: emailData.smtp_user ?? prev.smtp_user,
          smtp_from_name: emailData.from_name ?? prev.smtp_from_name,
          smtp_configured: emailData.smtp_configured ?? false,
          mode: emailData.mode ?? 'mock'
        }));
      }
      
      // Fetch UF config
      const ufRes = await fetch(`${API_URL}/api/uf/current`);
      if (ufRes.ok) {
        const ufData = await ufRes.json();
        setUfConfig(prev => ({ 
          ...prev, 
          valor_actual: ufData.valor,
          fecha_valor_actual: ufData.fecha,
          fuente_actual: ufData.fuente
        }));
      }
    } catch (error) {
      console.error('Error fetching configurations:', error);
      toast.error('Error al cargar configuraciones');
    } finally {
      setLoading(false);
    }
  };
  
  const saveTransbankConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/transbank`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          commerce_code: transbankConfig.commerce_code,
          api_key: transbankConfig.api_key,
          environment: transbankConfig.is_production ? 'production' : 'integration'
        })
      });
      
      if (res.ok) {
        toast.success('Configuración de Transbank guardada');
        fetchConfigurations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };
  
  const saveEmailConfig = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          smtp_host: emailConfig.smtp_host,
          smtp_port: emailConfig.smtp_port,
          smtp_user: emailConfig.smtp_user,
          smtp_password: emailConfig.smtp_password,
          smtp_from_name: emailConfig.smtp_from_name
        })
      });
      
      if (res.ok) {
        toast.success('Configuración de Email guardada');
        fetchConfigurations();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al guardar');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setSaving(false);
    }
  };
  
  const testEmailConfig = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/email/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          test_email: user?.email
        })
      });
      
      if (res.ok) {
        toast.success('Email de prueba enviado');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Error al enviar email de prueba');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTesting(false);
    }
  };
  
  const syncUF = async () => {
    setTesting(true);
    try {
      const res = await fetch(`${API_URL}/api/uf/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`UF actualizada: ${data.valor_formateado || data.valor}`);
        fetchConfigurations();
      } else {
        toast.error('Error al sincronizar UF');
      }
    } catch (error) {
      toast.error('Error de conexión');
    } finally {
      setTesting(false);
    }
  };
  
  // Test Transbank Payment Flow
  const startPaymentTest = async () => {
    setShowPaymentTest(true);
    setPaymentTestStatus('creating');
    setPaymentTestData(null);
    
    try {
      const res = await fetch(`${API_URL}/api/admin/settings/transbank/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          amount: 1000, // 1000 CLP = monto mínimo de prueba
          description: 'Prueba de Configuración Transbank'
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setPaymentTestData(data);
        setPaymentTestStatus('pending');
        
        // Abrir WebPay en nueva pestaña si hay URL
        if (data.checkout_url) {
          window.open(data.checkout_url, '_blank');
        }
      } else {
        const error = await res.json();
        setPaymentTestStatus('failed');
        setPaymentTestData({ error: error.detail || 'Error al crear transacción de prueba' });
      }
    } catch (error) {
      setPaymentTestStatus('failed');
      setPaymentTestData({ error: 'Error de conexión' });
    }
  };
  
  const checkPaymentStatus = async () => {
    if (!paymentTestData?.buy_order) return;
    
    try {
      const res = await fetch(`${API_URL}/api/transbank/status/${paymentTestData.buy_order}`);
      
      if (res.ok) {
        const data = await res.json();
        
        if (data.status === 'approved' || data.status === 'AUTHORIZED') {
          setPaymentTestStatus('success');
          setPaymentTestData(prev => ({ ...prev, ...data }));
          toast.success('¡Pago de prueba exitoso!');
        } else if (data.status === 'failed' || data.status === 'REJECTED') {
          setPaymentTestStatus('failed');
          setPaymentTestData(prev => ({ ...prev, ...data }));
        } else {
          toast.info('Pago aún pendiente. Completa el flujo en WebPay.');
        }
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };
  
  if (!isSuperAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card className="w-96">
          <CardContent className="pt-6 text-center">
            <Shield className="w-12 h-12 mx-auto text-red-500 mb-4" />
            <h3 className="text-lg font-semibold mb-2">Acceso Restringido</h3>
            <p className="text-slate-500">Solo los Super Administradores pueden acceder a esta sección.</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Wrench className="w-6 h-6" />
            Configuración del Sistema
          </h1>
          <p className="text-slate-500 mt-1">
            Administra las credenciales e integraciones de SimuClinic
          </p>
        </div>
      </div>
      
      <Tabs defaultValue="payments" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4 lg:w-[640px]">
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Pagos
          </TabsTrigger>
          <TabsTrigger value="email" className="flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Email
          </TabsTrigger>
          <TabsTrigger value="uf" className="flex items-center gap-2">
            <span className="font-bold text-xs">UF</span>
            Valor UF
          </TabsTrigger>
          <TabsTrigger value="features" className="flex items-center gap-2" data-testid="tab-feature-flags">
            <span className="font-bold text-xs">🚀</span>
            Feature Flags
          </TabsTrigger>
        </TabsList>
        
        {/* Transbank Configuration */}
        <TabsContent value="payments">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-blue-500" />
                    Transbank WebPay Plus
                  </CardTitle>
                  <CardDescription>
                    Configuración de la pasarela de pagos para Chile
                  </CardDescription>
                </div>
                <Badge variant={transbankConfig.is_production ? "default" : "secondary"}>
                  {transbankConfig.is_production ? 'Producción' : 'Integración'}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="p-4 rounded-lg bg-slate-50 border">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  Estado Actual
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Ambiente:</span>
                    <span className="ml-2 font-medium">{transbankConfig.environment}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Código Comercio:</span>
                    <span className="ml-2 font-mono">{transbankConfig.commerce_code || 'No configurado'}</span>
                  </div>
                </div>
              </div>
              
              {/* Production Toggle */}
              <div className="flex items-center justify-between p-4 rounded-lg border border-amber-200 bg-amber-50">
                <div className="flex items-center gap-3">
                  <AlertTriangle className="w-5 h-5 text-amber-500" />
                  <div>
                    <p className="font-medium">Modo Producción</p>
                    <p className="text-sm text-slate-500">Activa para procesar pagos reales</p>
                  </div>
                </div>
                <Switch
                  checked={transbankConfig.is_production}
                  onCheckedChange={(checked) => setTransbankConfig(prev => ({ ...prev, is_production: checked }))}
                />
              </div>
              
              {/* Credentials Form */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="commerce_code">Código de Comercio</Label>
                  <Input
                    id="commerce_code"
                    placeholder="597XXXXXXXXX"
                    value={transbankConfig.commerce_code}
                    onChange={(e) => setTransbankConfig(prev => ({ ...prev, commerce_code: e.target.value }))}
                  />
                  <p className="text-xs text-slate-500">
                    Proporcionado por Transbank al contratar WebPay Plus
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="api_key">API Key</Label>
                  <div className="relative">
                    <Input
                      id="api_key"
                      type={showTransbankKey ? "text" : "password"}
                      placeholder="••••••••••••••••"
                      value={transbankConfig.api_key}
                      onChange={(e) => setTransbankConfig(prev => ({ ...prev, api_key: e.target.value }))}
                    />
                    <button
                      type="button"
                      onClick={() => setShowTransbankKey(!showTransbankKey)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showTransbankKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Clave secreta de la API de Transbank
                  </p>
                </div>
              </div>
              
              {/* Test Cards Info */}
              {!transbankConfig.is_production && (
                <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                  <h4 className="font-medium mb-2 flex items-center gap-2">
                    <TestTube className="w-4 h-4 text-blue-500" />
                    Tarjetas de Prueba (Modo Integración)
                  </h4>
                  <div className="text-sm space-y-1">
                    <p><span className="font-mono">4051 8856 0044 6623</span> - VISA Aprobada</p>
                    <p><span className="font-mono">5186 0595 5959 0568</span> - Mastercard Aprobada</p>
                    <p className="text-slate-500">CVV: 123 | Vencimiento: Cualquier fecha futura</p>
                  </div>
                </div>
              )}
              
              {/* Actions */}
              <div className="flex justify-between">
                <Button 
                  variant="outline" 
                  onClick={startPaymentTest}
                  disabled={!transbankConfig.commerce_code}
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Probar Pago ($1.000 CLP)
                </Button>
                <Button onClick={saveTransbankConfig} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* Email Configuration */}
        <TabsContent value="email">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Mail className="w-5 h-5 text-green-500" />
                    Notificaciones por Email
                  </CardTitle>
                  <CardDescription>
                    Configuración de Gmail/Google Workspace para envío de emails
                  </CardDescription>
                </div>
                <Badge variant={emailConfig.smtp_configured ? "default" : "secondary"}>
                  {emailConfig.smtp_configured ? (
                    <><Check className="w-3 h-3 mr-1" /> Configurado</>
                  ) : (
                    <><X className="w-3 h-3 mr-1" /> No configurado</>
                  )}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Status */}
              <div className="p-4 rounded-lg bg-slate-50 border">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-green-500" />
                  Estado Actual
                </h4>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-slate-500">Servidor SMTP:</span>
                    <span className="ml-2 font-medium">{emailConfig.smtp_host}:{emailConfig.smtp_port}</span>
                  </div>
                  <div>
                    <span className="text-slate-500">Modo:</span>
                    <span className="ml-2 font-medium">{emailConfig.mode === 'mock' ? 'Simulación' : 'Activo'}</span>
                  </div>
                </div>
              </div>
              
              {/* Credentials Form */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="smtp_host">Servidor SMTP</Label>
                    <Input
                      id="smtp_host"
                      value={emailConfig.smtp_host}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_host: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="smtp_port">Puerto</Label>
                    <Input
                      id="smtp_port"
                      type="number"
                      value={emailConfig.smtp_port}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_port: parseInt(e.target.value) }))}
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="smtp_user">Cuenta de Email</Label>
                  <Input
                    id="smtp_user"
                    type="email"
                    value={emailConfig.smtp_user}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_user: e.target.value }))}
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="smtp_password">Contraseña de Aplicación</Label>
                  <div className="relative">
                    <Input
                      id="smtp_password"
                      type={showEmailPassword ? "text" : "password"}
                      placeholder="••••••••••••••••"
                      value={emailConfig.smtp_password}
                      onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_password: e.target.value }))}
                    />
                    <button
                      type="button"
                      onClick={() => setShowEmailPassword(!showEmailPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showEmailPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-slate-500">
                    Para Gmail/Google Workspace, usa una Contraseña de Aplicación desde{' '}
                    <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:underline">
                      Google Admin Console
                    </a>
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="smtp_from_name">Nombre del Remitente</Label>
                  <Input
                    id="smtp_from_name"
                    value={emailConfig.smtp_from_name}
                    onChange={(e) => setEmailConfig(prev => ({ ...prev, smtp_from_name: e.target.value }))}
                  />
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-between">
                <Button variant="outline" onClick={testEmailConfig} disabled={testing || !emailConfig.smtp_password}>
                  {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <TestTube className="w-4 h-4 mr-2" />}
                  Enviar Email de Prueba
                </Button>
                <Button onClick={saveEmailConfig} disabled={saving}>
                  {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                  Guardar Configuración
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* UF Configuration */}
        <TabsContent value="uf">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <span className="w-6 h-6 rounded bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">UF</span>
                    Valor de la Unidad de Fomento
                  </CardTitle>
                  <CardDescription>
                    Sincronización automática del valor UF desde mindicador.cl
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Current Value */}
              <div className="p-6 rounded-lg bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-500 mb-1">Valor Actual</p>
                    <p className="text-3xl font-bold text-emerald-700">
                      ${ufConfig.valor_actual?.toLocaleString('es-CL')} CLP
                    </p>
                    <p className="text-sm text-slate-500 mt-1">
                      Última actualización: {ufConfig.fecha_valor_actual || 'N/A'}
                    </p>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className="mb-2">
                      Fuente: {ufConfig.fuente_actual}
                    </Badge>
                    <br />
                    <Button onClick={syncUF} disabled={testing} size="sm" className="mt-2">
                      {testing ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                      Sincronizar Ahora
                    </Button>
                  </div>
                </div>
              </div>
              
              {/* Info */}
              <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
                <h4 className="font-medium mb-2 flex items-center gap-2">
                  <Info className="w-4 h-4 text-blue-500" />
                  Sincronización Automática
                </h4>
                <p className="text-sm text-slate-600">
                  El valor de la UF se actualiza automáticamente cada día desde mindicador.cl. 
                  Todos los precios en SimuClinic están expresados en UF y se convierten a CLP 
                  usando este valor al momento del pago.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Feature Flags Tab (v10.7.1) */}
        <TabsContent value="features">
          <FeatureFlagsPanel />
        </TabsContent>
      </Tabs>
      
      {/* Payment Test Modal */}
      <Dialog open={showPaymentTest} onOpenChange={setShowPaymentTest}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="w-5 h-5 text-blue-500" />
              Prueba de Pago Transbank
            </DialogTitle>
            <DialogDescription>
              Se creará una transacción de prueba por $1.000 CLP
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {paymentTestStatus === 'creating' && (
              <div className="text-center py-8">
                <RefreshCw className="w-12 h-12 mx-auto text-blue-500 animate-spin mb-4" />
                <p className="text-slate-600">Creando transacción de prueba...</p>
              </div>
            )}
            
            {paymentTestStatus === 'pending' && paymentTestData && (
              <div className="space-y-4">
                <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-3">
                    <Clock className="w-5 h-5 text-amber-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-amber-800">Transacción Creada</p>
                      <p className="text-sm text-amber-700 mt-1">
                        Se abrió una nueva pestaña con WebPay. Completa el pago con una tarjeta de prueba.
                      </p>
                    </div>
                  </div>
                </div>
                
                <div className="p-4 rounded-lg bg-slate-50 border text-sm space-y-2">
                  <div className="flex justify-between">
                    <span className="text-slate-500">Orden:</span>
                    <span className="font-mono">{paymentTestData.buy_order}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Monto:</span>
                    <span className="font-medium">{paymentTestData.monto_clp_formateado || '$1.000'}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-slate-500">Ambiente:</span>
                    <Badge variant="secondary">{paymentTestData.environment || 'integration'}</Badge>
                  </div>
                </div>
                
                {paymentTestData.checkout_url && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => window.open(paymentTestData.checkout_url, '_blank')}
                  >
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir WebPay nuevamente
                  </Button>
                )}
                
                <div className="p-3 rounded bg-blue-50 text-sm">
                  <p className="font-medium text-blue-800 mb-1">Tarjeta de Prueba:</p>
                  <p className="font-mono text-blue-700">4051 8856 0044 6623</p>
                  <p className="text-blue-600">CVV: 123 | Exp: Cualquier fecha futura</p>
                  <p className="text-blue-600 mt-1">RUT: 11.111.111-1 | Clave: 123</p>
                </div>
              </div>
            )}
            
            {paymentTestStatus === 'success' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-10 h-10 text-green-500" />
                </div>
                <h3 className="text-lg font-semibold text-green-700 mb-2">¡Pago Exitoso!</h3>
                <p className="text-slate-600 mb-4">
                  La configuración de Transbank está funcionando correctamente.
                </p>
                {paymentTestData && (
                  <div className="p-4 rounded-lg bg-slate-50 border text-sm text-left">
                    <div className="flex justify-between mb-1">
                      <span className="text-slate-500">Código Autorización:</span>
                      <span className="font-mono">{paymentTestData.authorization_code || 'N/A'}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-slate-500">Tipo Tarjeta:</span>
                      <span>{paymentTestData.card_type || paymentTestData.payment_type_code || 'N/A'}</span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {paymentTestStatus === 'failed' && (
              <div className="text-center py-6">
                <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h3 className="text-lg font-semibold text-red-700 mb-2">Error en el Pago</h3>
                <p className="text-slate-600 mb-4">
                  {paymentTestData?.error || 'No se pudo completar la transacción de prueba.'}
                </p>
                <p className="text-sm text-slate-500">
                  Verifica las credenciales de Transbank e intenta nuevamente.
                </p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            {paymentTestStatus === 'pending' && (
              <Button variant="outline" onClick={checkPaymentStatus}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Verificar Estado
              </Button>
            )}
            <Button 
              variant={paymentTestStatus === 'success' ? 'default' : 'outline'}
              onClick={() => {
                setShowPaymentTest(false);
                setPaymentTestStatus('idle');
                setPaymentTestData(null);
              }}
            >
              {paymentTestStatus === 'success' ? 'Cerrar' : 'Cancelar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
